import { useState, useEffect } from 'react';
import { toast } from 'sonner';

export function useBodyData({ isAuthenticated, startSync, completeSync, failSync }) {
  const [bodyDataLoaded, setBodyDataLoaded] = useState(false);
  
  const [currentBodyweight, setCurrentBodyweight] = useState(() => {
    return localStorage.getItem('gym_notebook_persistent_bodyweight') || '';
  });
  const [newBodyweight, setNewBodyweight] = useState(() => {
    return localStorage.getItem('gym_notebook_persistent_bodyweight') || '';
  });
  const [weightUnit, setWeightUnit] = useState(() => {
    return localStorage.getItem('gym_notebook_persistent_weight_unit') || 'kg';
  });
  const [bodyHeight, setBodyHeight] = useState(() => {
    return localStorage.getItem('gym_notebook_persistent_height') || '';
  });
  const [heightUnit, setHeightUnit] = useState(() => {
    return localStorage.getItem('gym_notebook_persistent_height_unit') || 'cm';
  });
  const [userAge, setUserAge] = useState(() => {
    return localStorage.getItem('gym_notebook_persistent_age') || '';
  });
  const [userGender, setUserGender] = useState(() => {
    return localStorage.getItem('gym_notebook_persistent_gender') || 'male';
  });
  const [waistMeasurement, setWaistMeasurement] = useState(() => {
    return localStorage.getItem('gym_notebook_waist') || '';
  });
  const [waistUnit, setWaistUnit] = useState(() => {
    return localStorage.getItem('gym_notebook_waist_unit') || 'cm';
  });
  const [weightGoal, setWeightGoal] = useState(() => {
    return localStorage.getItem('gym_notebook_weight_goal') || 'maintain';
  });
  const [weightGoalStartDate, setWeightGoalStartDate] = useState(() => {
    return localStorage.getItem('gym_notebook_weight_goal_start') || '';
  });
  const [showGoalInfo, setShowGoalInfo] = useState(null);
  const [bodyMeasurements, setBodyMeasurements] = useState(() => {
    const saved = localStorage.getItem('gym_notebook_persistent_measurements');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { /* ignore */ }
    }
    return { chest: '', waist: '', biceps: '', thigh: '', calve: '' };
  });
  const [measurementUnit, setMeasurementUnit] = useState(() => {
    return localStorage.getItem('gym_notebook_persistent_measurement_unit') || 'cm';
  });
  const [strideLength, setStrideLength] = useState(() => {
    const saved = localStorage.getItem('gym_notebook_walking_stride_length');
    return saved ? parseInt(saved) : 75;
  });

  // Load body data from backend
  useEffect(() => {
    const loadBodyweight = async () => {
      const token = localStorage.getItem('gym_notebook_auth_token');
      if (token) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          const response = await fetch(`/api/v2/data/user-bodyweight`, {
            headers: { 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          if (response.ok) {
            const data = await response.json();
            if (data.bodyweight) {
              setCurrentBodyweight(data.bodyweight);
              setNewBodyweight(data.bodyweight);
              localStorage.setItem('gym_notebook_persistent_bodyweight', data.bodyweight);
            }
            if (data.weightUnit) {
              setWeightUnit(data.weightUnit);
              localStorage.setItem('gym_notebook_persistent_weight_unit', data.weightUnit);
            }
            if (data.bodyMeasurements) {
              setBodyMeasurements(data.bodyMeasurements);
              localStorage.setItem('gym_notebook_persistent_measurements', JSON.stringify(data.bodyMeasurements));
            }
            if (data.measurementUnit) {
              setMeasurementUnit(data.measurementUnit);
              localStorage.setItem('gym_notebook_persistent_measurement_unit', data.measurementUnit);
            }
            if (data.height && data.height !== '') {
              setBodyHeight(data.height);
              localStorage.setItem('gym_notebook_persistent_height', data.height);
            }
            if (data.heightUnit) {
              setHeightUnit(data.heightUnit);
              localStorage.setItem('gym_notebook_persistent_height_unit', data.heightUnit);
            }
            if (data.age && data.age !== '') {
              setUserAge(data.age);
              localStorage.setItem('gym_notebook_persistent_age', data.age);
            }
            if (data.gender && data.gender !== '') {
              setUserGender(data.gender);
              localStorage.setItem('gym_notebook_persistent_gender', data.gender);
            }
            if (data.weightGoal) {
              setWeightGoal(data.weightGoal);
              localStorage.setItem('gym_notebook_weight_goal', data.weightGoal);
            }
            if (data.weightGoalStartDate) {
              setWeightGoalStartDate(data.weightGoalStartDate);
              localStorage.setItem('gym_notebook_weight_goal_start', data.weightGoalStartDate);
            }
            if (data.strideLength) {
              setStrideLength(data.strideLength);
              localStorage.setItem('gym_notebook_walking_stride_length', data.strideLength.toString());
            }
            if (data.waist && data.waist !== '') {
              setWaistMeasurement(data.waist);
              localStorage.setItem('gym_notebook_waist', data.waist);
            }
            if (data.waistUnit) {
              setWaistUnit(data.waistUnit);
              localStorage.setItem('gym_notebook_waist_unit', data.waistUnit);
            }
            setBodyDataLoaded(true);
            return;
          }
        } catch (e) {
          console.error('Error loading bodyweight from backend:', e);
        }
      }
      // Fallback to localStorage
      const persistentWeight = localStorage.getItem('gym_notebook_persistent_bodyweight');
      const persistentUnit = localStorage.getItem('gym_notebook_persistent_weight_unit');
      if (persistentWeight) {
        setCurrentBodyweight(persistentWeight);
        setNewBodyweight(persistentWeight);
      }
      if (persistentUnit) setWeightUnit(persistentUnit);
      setBodyDataLoaded(true);
    };
    loadBodyweight();
  }, []);

  const handleUpdateBodyweight = async () => {
    if (!bodyDataLoaded) {
      toast.error('Please wait for data to load');
      return;
    }
    if (newBodyweight) {
      localStorage.setItem('gym_notebook_persistent_bodyweight', newBodyweight);
    }
    localStorage.setItem('gym_notebook_persistent_weight_unit', weightUnit);
    if (bodyHeight) localStorage.setItem('gym_notebook_persistent_height', bodyHeight);
    if (heightUnit) localStorage.setItem('gym_notebook_persistent_height_unit', heightUnit);
    if (userAge) localStorage.setItem('gym_notebook_persistent_age', userAge);
    if (userGender) localStorage.setItem('gym_notebook_persistent_gender', userGender);
    localStorage.setItem('gym_notebook_walking_stride_length', strideLength.toString());

    if (waistMeasurement) {
      localStorage.setItem('gym_notebook_waist', waistMeasurement);
      localStorage.setItem('gym_notebook_waist_unit', waistUnit);
      const waistHistory = JSON.parse(localStorage.getItem('gym_notebook_waist_history') || '[]');
      waistHistory.unshift({ date: new Date().toISOString(), waist: parseFloat(waistMeasurement), unit: waistUnit });
      localStorage.setItem('gym_notebook_waist_history', JSON.stringify(waistHistory.slice(0, 100)));
    }

    // Always record weight in body measurements history for the Stats weight chart
    const historyKey = 'gym_notebook_body_measurements_history';
    const historyData = JSON.parse(localStorage.getItem(historyKey) || '[]');
    historyData.unshift({ date: new Date().toISOString(), bodyweight: newBodyweight, measurements: bodyMeasurements, unit: measurementUnit });
    localStorage.setItem(historyKey, JSON.stringify(historyData.slice(0, 50)));

    const token = localStorage.getItem('gym_notebook_auth_token');
    if (token) {
      startSync();
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        const response = await fetch(`/api/v2/data/user-bodyweight`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            bodyweight: newBodyweight, weightUnit, bodyMeasurements, measurementUnit,
            height: bodyHeight, heightUnit, age: userAge, gender: userGender,
            weightGoal, weightGoalStartDate, strideLength,
            waist: waistMeasurement, waistUnit
          })
        });
        clearTimeout(timeoutId);
        if (response.ok) { completeSync(); } else { failSync(new Error(`HTTP ${response.status}`)); }
      } catch (e) {
        console.error('Error syncing bodyweight:', e);
        failSync(e);
      }
    }
    setCurrentBodyweight(newBodyweight);
    toast.success('Body info saved');
  };

  const handleSaveMeasurements = async () => {
    localStorage.setItem('gym_notebook_persistent_measurements', JSON.stringify(bodyMeasurements));
    localStorage.setItem('gym_notebook_persistent_measurement_unit', measurementUnit);
    if (Object.values(bodyMeasurements).some(v => v)) {
      const historyKey = 'gym_notebook_body_measurements_history';
      const historyData = JSON.parse(localStorage.getItem(historyKey) || '[]');
      historyData.unshift({ date: new Date().toISOString(), bodyweight: currentBodyweight, measurements: bodyMeasurements, unit: measurementUnit });
      localStorage.setItem(historyKey, JSON.stringify(historyData.slice(0, 50)));
    }
    window.dispatchEvent(new Event('bodyMeasurementsUpdated'));
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (token) {
      try {
        await fetch(`/api/v2/data/user-bodyweight`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ bodyMeasurements, measurementUnit })
        });
      } catch (e) { console.error('Error syncing measurements to backend:', e); }
    }
    toast.success('Body measurements saved');
  };

  return {
    bodyDataLoaded,
    currentBodyweight, setCurrentBodyweight,
    newBodyweight, setNewBodyweight,
    weightUnit, setWeightUnit,
    bodyHeight, setBodyHeight,
    heightUnit, setHeightUnit,
    userAge, setUserAge,
    userGender, setUserGender,
    waistMeasurement, setWaistMeasurement,
    waistUnit, setWaistUnit,
    weightGoal, setWeightGoal,
    weightGoalStartDate, setWeightGoalStartDate,
    showGoalInfo, setShowGoalInfo,
    bodyMeasurements, setBodyMeasurements,
    measurementUnit, setMeasurementUnit,
    strideLength, setStrideLength,
    handleUpdateBodyweight,
    handleSaveMeasurements,
  };
}
